import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import cn from 'classnames'
import { isMobile } from 'lib/device'
import { UserCheck, UserPlus } from 'lucide-react'
import api from 'api/api'
import queryClient from 'api/queryClient'
import useGetUser from 'refactor/hooks/api/useGetUser'
import { cacheUser, resolveUser } from 'state/cache'
import UserIcon from 'components/users/UserIcon'
import MessageUserButton from 'components/users/MessageUserButton'
import styles from './user-hover-card.module.scss'

const OPEN_DELAY_MS = 500
const CLOSE_DELAY_MS = 300
const CARD_WIDTH_PX = 320
const ANCHOR_GAP_PX = 8
const VIEWPORT_MARGIN_PX = 12
// Rough height used only to pick above/below placement before the card has
// rendered; the open card is anchored by its near edge, so real height may
// differ without overlapping the avatar.
const ESTIMATED_CARD_HEIGHT_PX = 300

interface HoverCardUser {
    id?: string | number
    name?: string
    username?: string
    bio?: string
    description?: string
    followers_count?: number
    following_count?: number
    is_followed_by_current_user?: boolean
    is_following_current_user?: boolean
    [key: string]: unknown
}

interface UseUserHoverCardArgs {
    user?: HoverCardUser | null
    disable?: boolean
    onOpenStream?: (ev: React.MouseEvent) => void
    // Opens a thread (the direct message) from the trigger's panel; the card
    // itself renders in a portal with no panel of its own.
    onOpenQuest?: (questId: string, ev: React.MouseEvent) => void
    openOnPress?: boolean
}

type CardPlacement = { left: number; top?: number; bottom?: number }

// X-style profile hover card. Returns props to spread on the avatar trigger
// element plus a portal node to render alongside it. Callers can opt into the
// same card on touch devices, where a press replaces direct navigation.
export function useUserHoverCard({
    user,
    disable,
    onOpenStream,
    onOpenQuest,
    openOnPress = false,
}: UseUserHoverCardArgs) {
    const userId = user?.id ? String(user.id) : null
    const desktopHoverEnabled = !disable && !isMobile && !!userId
    const mobilePressEnabled = !disable && isMobile && openOnPress && !!userId
    const enabled = desktopHoverEnabled || mobilePressEnabled

    const [open, setOpen] = useState(false)
    const [placement, setPlacement] = useState<CardPlacement | null>(null)
    const triggerRef = useRef<HTMLElement | null>(null)
    const cardRef = useRef<HTMLDivElement | null>(null)
    const openTimerRef = useRef<number | null>(null)
    const closeTimerRef = useRef<number | null>(null)
    // Set when hover opened the card: the next avatar press confirms it
    // instead of toggling it shut.
    const openedByHoverRef = useRef(false)

    const clearOpenTimer = useCallback(() => {
        if (openTimerRef.current) {
            window.clearTimeout(openTimerRef.current)
            openTimerRef.current = null
        }
    }, [])

    const clearCloseTimer = useCallback(() => {
        if (closeTimerRef.current) {
            window.clearTimeout(closeTimerRef.current)
            closeTimerRef.current = null
        }
    }, [])

    const close = useCallback(() => {
        clearOpenTimer()
        clearCloseTimer()
        openedByHoverRef.current = false
        setOpen(false)
    }, [clearOpenTimer, clearCloseTimer])

    const openNow = useCallback(() => {
        const trigger = triggerRef.current
        if (!trigger) return
        const rect = trigger.getBoundingClientRect()
        if (!rect.width && !rect.height) return

        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        const cardWidth = Math.min(CARD_WIDTH_PX, viewportWidth - VIEWPORT_MARGIN_PX * 2)
        const left = Math.max(
            VIEWPORT_MARGIN_PX,
            Math.min(
                rect.left + rect.width / 2 - cardWidth / 2,
                viewportWidth - cardWidth - VIEWPORT_MARGIN_PX,
            ),
        )
        const fitsBelow =
            rect.bottom + ANCHOR_GAP_PX + ESTIMATED_CARD_HEIGHT_PX <=
            viewportHeight - VIEWPORT_MARGIN_PX
        setPlacement(
            fitsBelow
                ? { left, top: rect.bottom + ANCHOR_GAP_PX }
                : { left, bottom: viewportHeight - rect.top + ANCHOR_GAP_PX },
        )
        setOpen(true)
    }, [])

    const onTriggerMouseEnter = useCallback(() => {
        clearCloseTimer()
        if (open || openTimerRef.current) return
        openTimerRef.current = window.setTimeout(() => {
            openTimerRef.current = null
            openedByHoverRef.current = true
            openNow()
        }, OPEN_DELAY_MS)
    }, [open, openNow, clearCloseTimer])

    const scheduleClose = useCallback(() => {
        clearOpenTimer()
        clearCloseTimer()
        closeTimerRef.current = window.setTimeout(() => {
            closeTimerRef.current = null
            openedByHoverRef.current = false
            setOpen(false)
        }, CLOSE_DELAY_MS)
    }, [clearOpenTimer, clearCloseTimer])

    // A press on the avatar toggles the card, on phones and desktop alike: it
    // never navigates. The card's own Profile / Message controls do that.
    // The first press after a hover-open keeps the card up; the next closes it.
    const openFromPress = useCallback(
        (ev: React.MouseEvent) => {
            if (!enabled) return false
            ev.preventDefault()
            ev.stopPropagation()
            if (open && openedByHoverRef.current) {
                openedByHoverRef.current = false
                clearCloseTimer()
            } else if (open) close()
            else {
                clearOpenTimer()
                clearCloseTimer()
                openedByHoverRef.current = false
                openNow()
            }
            return true
        },
        [clearCloseTimer, clearOpenTimer, close, enabled, open, openNow],
    )

    // A repositioned card would detach from its avatar, so any scroll or
    // resize simply dismisses it (panels scroll internally: capture phase).
    useEffect(() => {
        if (!open) return
        const dismiss = () => close()
        window.addEventListener('scroll', dismiss, true)
        window.addEventListener('resize', dismiss)
        return () => {
            window.removeEventListener('scroll', dismiss, true)
            window.removeEventListener('resize', dismiss)
        }
    }, [open, close])

    useEffect(() => {
        if (!open) return

        const dismissOnOutsidePress = (event: PointerEvent) => {
            const node = event.target as Node
            if (triggerRef.current?.contains(node) || cardRef.current?.contains(node)) return
            // On phones, treat the first outside press as modal dismissal only.
            // Without this, tapping the artwork behind the card also opens
            // fullscreen. On desktop the click goes through as well.
            if (mobilePressEnabled) {
                event.preventDefault()
                event.stopPropagation()
            }
            close()
        }

        document.addEventListener('pointerdown', dismissOnOutsidePress, true)
        return () => document.removeEventListener('pointerdown', dismissOnOutsidePress, true)
    }, [close, mobilePressEnabled, open])

    useEffect(() => {
        return () => {
            clearOpenTimer()
            clearCloseTimer()
        }
    }, [clearOpenTimer, clearCloseTimer])

    // If the trigger is recycled for a different user (virtualized rows),
    // never leave the previous user's card up.
    useEffect(() => {
        close()
    }, [userId, close])

    const setTriggerRef = useCallback((node: HTMLElement | null) => {
        triggerRef.current = node
    }, [])

    const triggerProps = enabled
        ? {
              ref: setTriggerRef,
              ...(desktopHoverEnabled
                  ? {
                        onMouseEnter: onTriggerMouseEnter,
                        onMouseLeave: scheduleClose,
                    }
                  : {}),
          }
        : {}

    const portal =
        enabled && open && placement && userId
            ? createPortal(
                  <UserHoverCardPanel
                      userId={userId}
                      baseUser={user}
                      cardRef={cardRef}
                      placement={placement}
                      onMouseEnter={clearCloseTimer}
                      onMouseLeave={scheduleClose}
                      onOpenStream={onOpenStream}
                      onOpenQuest={onOpenQuest}
                      close={close}
                  />,
                  document.body,
              )
            : null

    return { enabled, triggerProps, portal, openFromPress }
}

interface UserHoverCardPanelProps {
    userId: string
    baseUser?: HoverCardUser | null
    cardRef: React.RefObject<HTMLDivElement | null>
    placement: CardPlacement
    onMouseEnter: () => void
    onMouseLeave: () => void
    onOpenStream?: (ev: React.MouseEvent) => void
    onOpenQuest?: (questId: string, ev: React.MouseEvent) => void
    close: () => void
}

function UserHoverCardPanel({
    userId,
    baseUser,
    cardRef,
    placement,
    onMouseEnter,
    onMouseLeave,
    onOpenStream,
    onOpenQuest,
    close,
}: UserHoverCardPanelProps) {
    // Fresh profile fetch (react-query dedupes across cards/panels); the lean
    // embedded user renders name/avatar instantly while it loads.
    const { user: fetchedUser } = useGetUser(userId)
    const resolvedUser = useMemo(() => {
        try {
            return resolveUser(baseUser, userId)
        } catch {
            return baseUser
        }
    }, [baseUser, userId])
    const displayUser: HoverCardUser = { ...(resolvedUser || {}), ...(fetchedUser || {}) }

    const [isFollowing, setIsFollowing] = useState(false)
    const [isLoadingFollow, setIsLoadingFollow] = useState(false)
    const [followersCount, setFollowersCount] = useState<number | null>(null)
    const [followingCount, setFollowingCount] = useState<number | null>(null)

    const hasKnownFollowState = useMemo(() => {
        if (typeof fetchedUser?.is_followed_by_current_user === 'boolean') return true
        return typeof displayUser.is_followed_by_current_user === 'boolean'
    }, [fetchedUser, displayUser.is_followed_by_current_user])

    useEffect(() => {
        const sourceUser = fetchedUser || resolvedUser
        if (!sourceUser) return
        if (typeof sourceUser.is_followed_by_current_user === 'boolean') {
            setIsFollowing(sourceUser.is_followed_by_current_user)
        }
        if (typeof sourceUser.followers_count === 'number') {
            setFollowersCount(sourceUser.followers_count)
        }
        if (typeof sourceUser.following_count === 'number') {
            setFollowingCount(sourceUser.following_count)
        }
    }, [fetchedUser, resolvedUser])

    const displayName = displayUser.username || displayUser.name || 'User'
    const bio = String(displayUser.bio || displayUser.description || '')
    const followsYou = Boolean(gon.currentUser && displayUser.is_following_current_user)

    const handleOpenStream = (ev: React.MouseEvent) => {
        onOpenStream?.(ev)
        close()
    }

    const handleFollowToggle = async (ev: React.MouseEvent) => {
        ev.preventDefault()
        ev.stopPropagation()

        if (isLoadingFollow || !hasKnownFollowState) return

        setIsLoadingFollow(true)
        try {
            const nextIsFollowing = !isFollowing
            // Self-follows are excluded from displayed counts (they're the
            // own-posts-in-home preference), so toggling one leaves counts as-is.
            const isOwnCard = String(userId) === String(gon.currentUser?.id)
            const followerDelta = isOwnCard ? 0 : nextIsFollowing ? 1 : -1
            const nextFollowersCount = Math.max(0, (followersCount || 0) + followerDelta)
            if (nextIsFollowing) {
                await api.followUser(userId)
            } else {
                await api.unfollowUser(userId)
            }

            setIsFollowing(nextIsFollowing)
            setFollowersCount(nextFollowersCount)

            // Keep both caches in sync so profile panels and other cards agree.
            cacheUser(
                {
                    id: userId,
                    is_followed_by_current_user: nextIsFollowing,
                    followers_count: nextFollowersCount,
                },
                true,
            )
            queryClient.setQueryData<HoverCardUser | null>(['user', userId], oldUser => {
                if (!oldUser) return oldUser
                const oldFollowersCount =
                    typeof oldUser.followers_count === 'number' ? oldUser.followers_count : 0
                return {
                    ...oldUser,
                    is_followed_by_current_user: nextIsFollowing,
                    followers_count: Math.max(0, oldFollowersCount + followerDelta),
                }
            })
        } catch (error) {
            console.error('Error toggling follow from hover card:', error)
        } finally {
            setIsLoadingFollow(false)
        }
    }

    const positionStyle: React.CSSProperties = {
        left: placement.left,
        top: placement.top,
        bottom: placement.bottom,
    }

    return (
        <div
            ref={cardRef}
            className={styles.userHoverCardComp}
            style={positionStyle}
            role="dialog"
            aria-label={`${displayName} profile`}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={ev => ev.stopPropagation()}
        >
            <div className={styles.header}>
                <div className={styles.avatarButton} onClick={handleOpenStream}>
                    <UserIcon user={displayUser} size="large" contextStyles={styles.avatar} />
                </div>

                {gon.currentUser && (
                    <div className={styles.headerActions}>
                        <button
                            className={cn(
                                styles.followButton,
                                isFollowing && styles.followingButton,
                            )}
                            onClick={handleFollowToggle}
                            disabled={isLoadingFollow || !hasKnownFollowState}
                        >
                            {isFollowing ? (
                                <>
                                    <UserCheck size={15} />
                                    <span>Following</span>
                                </>
                            ) : (
                                <>
                                    <UserPlus size={15} />
                                    <span>Follow</span>
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>

            <div className={styles.nameRow}>
                <span className={styles.userName} onClick={handleOpenStream}>
                    {displayName}
                </span>
                {followsYou && <span className={styles.followsYouBadge}>Follows you</span>}
            </div>

            {bio && <div className={styles.bio}>{bio}</div>}

            {(followingCount !== null || followersCount !== null) && (
                <div className={styles.statsRow}>
                    <span className={styles.stat}>
                        <span className={styles.statCount}>{followingCount ?? 0}</span>
                        <span className={styles.statLabel}>Following</span>
                    </span>
                    <span className={styles.stat}>
                        <span className={styles.statCount}>{followersCount ?? 0}</span>
                        <span className={styles.statLabel}>Followers</span>
                    </span>
                </div>
            )}

            {gon.currentUser && onOpenQuest && (
                <MessageUserButton
                    user={displayUser}
                    onOpenQuest={(questId, ev) => {
                        onOpenQuest(questId, ev)
                        close()
                    }}
                    className={styles.messageWideButton}
                    iconSize={15}
                    testId="user-hover-card-message-button"
                />
            )}

            <button type="button" className={styles.profileButton} onClick={handleOpenStream}>
                Profile
            </button>
        </div>
    )
}
